import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError, fetchBlobUrl } from "@/lib/api";
import { AuthImage } from "@/components/AuthImage";
import { PaywallDialog } from "@/components/PaywallDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { FolderLock, Plus, Trash2, FileText, ShieldCheck, Download } from "lucide-react";

const STATUS = {
  ok: { label: "Valide", cls: "bg-success/10 text-success border-success/30" },
  soon: { label: "Bientôt périmé", cls: "bg-amber-100 text-amber-700 border-amber-300" },
  expired: { label: "Périmé", cls: "bg-danger/10 text-danger border-danger/30" },
};

export default function Vault() {
  const { user, refresh } = useAuth();
  const fileRef = useRef(null);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [expiration, setExpiration] = useState("");
  const [verify, setVerify] = useState(false);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paywall, setPaywall] = useState(false);
  const [paywallReason, setPaywallReason] = useState("");

  const load = () => api.get("/vault").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!file || !label) { toast.error("Ajoutez un fichier et un intitulé."); return; }
    setLoading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("label", label);
    if (expiration) fd.append("expiration_date", expiration);
    fd.append("verify", verify ? "true" : "false");
    try {
      await api.post("/vault", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Justificatif ajouté au coffre-fort.");
      setOpen(false); setLabel(""); setExpiration(""); setFile(null); setVerify(false);
      load(); refresh();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 402) {
        setPaywallReason(detail?.message || "Coffre-fort plein.");
        setPaywall(true); setOpen(false);
      } else {
        toast.error(formatApiError(detail) || "Échec de l'ajout.");
      }
    } finally { setLoading(false); }
  };

  const remove = async (id) => {
    await api.delete(`/vault/${id}`);
    toast.success("Document retiré.");
    load(); refresh();
  };

  const download = async (it) => {
    const url = await fetchBlobUrl(`/vault/${it.id}/file`);
    window.open(url, "_blank");
  };

  const isImage = (ct) => ct?.startsWith("image/");

  return (
    <div className="space-y-6 adminia-fade-in">
      <PaywallDialog open={paywall} onOpenChange={setPaywall} reason={paywallReason} />
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-heading font-bold text-2xl sm:text-3xl">Coffre-fort</h1>
          <p className="text-muted-foreground mt-1">Vos justificatifs réutilisables, avec alertes de péremption.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full bg-brand hover:bg-brand/90 gap-2 shrink-0" data-testid="vault-add-btn">
              <Plus className="h-4 w-4" /> Ajouter
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="vault-add-dialog">
            <DialogHeader><DialogTitle className="font-heading">Ajouter un justificatif</DialogTitle>
              <DialogDescription>Importez une image ou un PDF et rangez-le dans votre coffre-fort.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Fichier (image ou PDF)</Label>
                <input ref={fileRef} type="file" accept="image/*,application/pdf"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="mt-1 block w-full text-sm" data-testid="vault-file-input" />
              </div>
              <div>
                <Label htmlFor="v-label">Intitulé</Label>
                <Input id="v-label" value={label} className="h-11 mt-1" placeholder="Ex : Justificatif de domicile"
                  onChange={(e) => setLabel(e.target.value)} data-testid="vault-label-input" />
              </div>
              <div>
                <Label htmlFor="v-exp">Date d'expiration (optionnel)</Label>
                <Input id="v-exp" type="date" value={expiration} className="h-11 mt-1"
                  onChange={(e) => setExpiration(e.target.value)} data-testid="vault-exp-input" />
              </div>
              <div className="flex items-center justify-between rounded-lg bg-surface p-3">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-brand" />
                  <span className="text-sm">Vérification IA du type/date {!user?.is_premium && "(Premium)"}</span>
                </div>
                <Switch checked={verify} onCheckedChange={setVerify} disabled={!user?.is_premium} data-testid="vault-verify-switch" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={submit} disabled={loading}
                className="w-full rounded-full bg-brand hover:bg-brand/90" data-testid="vault-submit-btn">
                {loading ? "Ajout…" : "Ajouter au coffre-fort"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {items.length === 0 ? (
        <Card className="p-12 text-center border-dashed">
          <div className="h-16 w-16 rounded-2xl bg-brand/10 flex items-center justify-center mx-auto mb-4">
            <FolderLock className="h-8 w-8 text-brand" />
          </div>
          <p className="font-medium">Votre coffre-fort est vide</p>
          <p className="text-sm text-muted-foreground mt-1">Ajoutez vos justificatifs pour les retrouver facilement.</p>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it) => {
            const st = STATUS[it.status] || STATUS.ok;
            return (
              <Card key={it.id} className="overflow-hidden" data-testid={`vault-item-${it.id}`}>
                <div className="h-36 bg-surface flex items-center justify-center">
                  {isImage(it.content_type) ? (
                    <AuthImage path={`/vault/${it.id}/file`} alt={it.label} className="h-36 w-full object-cover" />
                  ) : (
                    <FileText className="h-12 w-12 text-brand/60" />
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-medium truncate">{it.label}</p>
                    {it.expiration_date && (
                      <Badge variant="outline" className={st.cls + " shrink-0"}>{st.label}</Badge>
                    )}
                  </div>
                  {it.expiration_date && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Expire le {new Date(it.expiration_date).toLocaleDateString("fr-FR")}
                    </p>
                  )}
                  {it.verification?.type && (
                    <p className="text-xs text-brand mt-1 inline-flex items-center gap-1">
                      <ShieldCheck className="h-3 w-3" /> {it.verification.type}
                    </p>
                  )}
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" variant="outline" className="rounded-full gap-1 flex-1"
                      onClick={() => download(it)} data-testid={`vault-download-${it.id}`}>
                      <Download className="h-3.5 w-3.5" /> Voir
                    </Button>
                    <Button size="sm" variant="ghost" className="rounded-full text-danger"
                      onClick={() => remove(it.id)} data-testid={`vault-delete-${it.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
